import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TradieHomeNew from './pages/TradieHomeNew';
import CaptureJobNotes from './pages/CaptureJobNotes';
import DraftScopeResult from './pages/DraftScopeResult';
import GenerateSWMS from './pages/GenerateSWMS';
import SWMSResult from './pages/SWMSResult';
import LeadResult from './pages/LeadResult';
import LeadDetail from './pages/LeadDetail';
import AllWork from './pages/AllWork';
import UpdateDetail from './pages/UpdateDetail';
import UnifiedWorkspace from './pages/UnifiedWorkspace';
import UnifiedCapture from './pages/UnifiedCapture';
import AdminDashboard from './pages/AdminDashboard';
import AdminUpdateDetail from './pages/AdminUpdateDetail';
import AdminTradies from './pages/AdminTradies';
import AdminSetupRequests from './pages/AdminSetupRequests';
import FeatureScopeQuote from './pages/FeatureScopeQuote';
import FeatureSWMS from './pages/FeatureSWMS';
import FeatureLeadsReferrals from './pages/FeatureLeadsReferrals';
import FeatureVariations from './pages/FeatureVariations';
import FeatureSiteUpdates from './pages/FeatureSiteUpdates';
import Features from './pages/Features';

// Loading component
const LoadingScreen = () => (
  <div className="min-h-screen bg-slate-100 flex items-center justify-center">
    <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Protected Route wrapper
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    // Redirect to appropriate dashboard based on role
    return <Navigate to={user?.role === 'ADMIN' ? '/admin' : '/tradie'} replace />;
  }

  return children;
};

// Public Route wrapper (redirects authenticated users)
const PublicRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to={user?.role === 'ADMIN' ? '/admin' : '/tradie'} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Landing Page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Feature Pages */}
      <Route path="/features" element={<Features />} />
      <Route path="/features/scope-costing-quote" element={<FeatureScopeQuote />} />
      <Route path="/features/swms-safety" element={<FeatureSWMS />} />
      <Route path="/features/leads-referrals" element={<FeatureLeadsReferrals />} />
      <Route path="/features/variations" element={<FeatureVariations />} />
      <Route path="/features/site-updates" element={<FeatureSiteUpdates />} />
      
      {/* Auth routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/forgot-password" 
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        } 
      />
      <Route 
        path="/reset-password" 
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        } 
      />

      {/* Tradie routes */}
      <Route
        path="/tradie"
        element={
          <ProtectedRoute requiredRole="TRADIE">
            <TradieHomeNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tradie/all-work"
        element={
          <ProtectedRoute requiredRole="TRADIE">
            <AllWork />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tradie/win"
        element={
          <ProtectedRoute requiredRole="TRADIE">
            <UnifiedWorkspace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tradie/deliver"
        element={
          <ProtectedRoute requiredRole="TRADIE">
            <UnifiedWorkspace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tradie/get-paid"
        element={
          <ProtectedRoute requiredRole="TRADIE">
            <UnifiedWorkspace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tradie/claim"
        element={
          <ProtectedRoute requiredRole="TRADIE">
            <UnifiedCapture />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tradie/log-update"
        element={
          <ProtectedRoute requiredRole="TRADIE">
            <UnifiedCapture />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tradie/capture-job-notes"
        element={
          <ProtectedRoute requiredRole="TRADIE">
            <CaptureJobNotes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tradie/draft-scope-result"
        element={
          <ProtectedRoute requiredRole="TRADIE">
            <DraftScopeResult />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tradie/generate-swms"
        element={
          <ProtectedRoute requiredRole="TRADIE">
            <GenerateSWMS />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tradie/swms-result"
        element={
          <ProtectedRoute requiredRole="TRADIE">
            <SWMSResult />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tradie/leads-referral"
        element={
          <ProtectedRoute requiredRole="TRADIE">
            <UnifiedCapture />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tradie/lead-result"
        element={
          <ProtectedRoute requiredRole="TRADIE">
            <LeadResult />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tradie/lead/:id"
        element={
          <ProtectedRoute requiredRole="TRADIE">
            <LeadDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tradie/my-updates"
        element={
          <ProtectedRoute requiredRole="TRADIE">
            <AllWork />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tradie/update/:id"
        element={
          <ProtectedRoute requiredRole="TRADIE">
            <UpdateDetail />
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRole="ADMIN">
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tradies"
        element={
          <ProtectedRoute requiredRole="ADMIN">
            <AdminTradies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/setup-requests"
        element={
          <ProtectedRoute requiredRole="ADMIN">
            <AdminSetupRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/update/:id"
        element={
          <ProtectedRoute requiredRole="ADMIN">
            <AdminUpdateDetail />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
